import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Observable, of, Subject, throwError } from 'rxjs';
import { fromPromise } from 'rxjs/internal-compatibility';
import { catchError, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { DESTINATION_TYPES } from '../../../constants/destinationTypes';
import { useConfirm } from '../../../../../modal/components/confirm-dialog-v1/ConfirmDialogV1Provider';
import useConfirmV2 from '../../../../../modal/hooks/useConfirmV2';
import { DestinationMode } from '../../../../../core/interfaces/destination-mode';
import { AuthService } from '../../../../../auth/services/auth.service';
import { DestinationService } from '../../../../../core/services/destination.service';
import {
  getBackendErrorCodeFromObj,
  getErrorMessageFromObj
} from '../../../../../core/utils/request';
import { useDraftPipelineStorage } from '../../../../pipeline/create-pipeline/hooks/useDraftPipelineStorage';
import { DestinationConnectionActionableErrorStatus } from '../../../interfaces/destination';
import { DestinationSettingsService } from './destination-settings.service';
import { getDestinationConfigFlatModel } from './utils';
import { TRACKER_DEST_CONFIG_UPDATE_WARNING } from '../../../edit-destination/tracking/destinationUpdateWarning';
import { SetupGuideLinkTrackingParams } from '../../../../../connector/connector-form/interfaces/setupGuideLink';
import { getDestinationModeKeywords } from '../../../../../connector/connector-form/utils/utils';
import { SetupGuideCommService } from '../../../../../docs/services/setup-guide-comm.service';
import { SettingsFormSubmissionMode } from '../../../../../connector/connector-form/interfaces/submissionMode';
import {
  TRACKER_DEST_CONFIG_SUBMIT,
  TRACKER_DEST_CONFIG_SUBMIT_FAILED,
  TRACKER_DEST_CONFIG_SUBMIT_FORM_ERROR,
  TRACKER_DEST_CONFIG_SUBMIT_START,
  TRACKER_DEST_CONFIG_SUBMIT_SUCCESS,
  TRACKER_DEST_CONFIG_TEST,
  TRACKER_DEST_CONFIG_TEST_FAIL,
  TRACKER_DEST_CONFIG_TEST_FORM_ERROR,
  TRACKER_DEST_CONFIG_TEST_START,
  TRACKER_DEST_CONFIG_TEST_SUCCESS,
  TRACKER_SOURCE_CONFIG_SUBMIT_FORM_ERROR_CLICK
} from '../../../../../connector/connector-form/tracking/connectorFormTracking';
import useAnalyticsTracker from '../../../../../analytics/hooks/useAnalyticsTracker';
import useService from '../../../../../injector/hooks/useService';
import useToaster from '../../../../../toaster/hooks/useToaster';
import ConfigDestinationType from '../../../destination-config-by-type/ConfigDestinationType';
import { Action, ActionKind, SettingFormProps, State } from './interfaces';
import { DestinationUpdateWarningDialog } from '../../../edit-destination/components/destination-update-warning-dialog/DestinationUpdateWarningDialog';

const reducer = (state: State, action: Action): State => {
  const { type, data } = action;

  switch (type) {
    case ActionKind.SET_FORM_SUCCESS: {
      return { ...state, formSuccess: data };
    }

    case ActionKind.SET_FORM_ERROR: {
      return { ...state, formError: data };
    }

    case ActionKind.CLEAR_FORM_ERROR_METADATA: {
      return {
        ...state,

        formError: undefined,
        formErrorCode: undefined,
        formErrorFor: undefined
      };
    }

    case ActionKind.UPDATE_FORM_ERROR_METADATA: {
      return { ...state, ...data };
    }

    case ActionKind.SET_CONNECTING: {
      return { ...state, connecting: data };
    }
    case ActionKind.SET_SUBMITTING: {
      return { ...state, submitting: data };
    }

    case ActionKind.CONNECTION_SUCCESS: {
      return { ...state, connecting: false, formSuccess: 'Connection successful' };
    }

    default:
      throw new Error(`Unsupported action type: ${type}`);
  }
};

export default function DestinationSettingsForm({
  destinationType,
  destinationMode,
  savedDestinationName,
  updateConnection,
  hevoEntityFor,
  destination,
  draftDestination,
  selectedExistingDestination,
  frozenFields,
  onConnectionError,
  onConnectionSuccess,
  onSubmit,
  onFormDirty,
  onFormFieldChange,
  onFormFieldFocusIn,
  onFormDataChanges,
  onInviteMember
}: SettingFormProps) {
  const [destinationUpdateWarningConfig, setDestinationUpdateWarningConfig] = useState<any>({
    visible: false
  });

  const _destroyed$ = useRef(new Subject());
  const _destinationUpdateWarningResponse$ = useRef(new Subject());
  const _destinationService = useService(DestinationService);
  const _analyticsTracker = useAnalyticsTracker();
  const _authService = useService(AuthService);

  const _destinationSettingService = useService(DestinationSettingsService);
  const _setupGuideCommService = useService(SetupGuideCommService);

  const toaster = useToaster();

  const { confirmV2 } = useConfirmV2();
  const { confirm } = useConfirm();

  const {
    getDraftDestinationDataFromStorage,
    clearDraftDestinationDataFromStorage
  } = useDraftPipelineStorage();

  const destinationTypeIdentifier = destinationType.dest_type;
  const [state, dispatch] = useReducer(reducer, {
    ..._setApplicableExperimentVariants()
  });
  const {
    connecting,
    submitting,
    isSnowflakeAutoFillExperimentVariant,
    formError,
    formErrorFor,
    formErrorCode,
    formSuccess
  } = state;

  function _setApplicableExperimentVariants() {
    const experimentVariants: any = {};

    return experimentVariants;
  }

  useEffect(() => {
    _setApplicableExperimentVariants();
  }, []);

  useEffect(() => {
    const destroyed = _destroyed$.current;
    return () => {
      destroyed.next();
      destroyed.complete();
    };
  }, []);

  const getSubmissionErrorTrackingData = error => ({
    reason: getErrorMessageFromObj(error),
    ...getDestinationTypeTrackingProps(),
    ...getHevoContextTrackingProps()
  });

  const getDestinationTypeTrackingProps = () => ({
    destinationType: destinationTypeIdentifier
  });

  const getHevoContextTrackingProps = () => ({
    context: hevoEntityFor.toString()
  });

  const onTestConnectionClick = () => {
    dispatch({ type: ActionKind.SET_FORM_SUCCESS, data: undefined });
    _clearFormErrorMetaData();

    _analyticsTracker.eventTrack({
      action: TRACKER_DEST_CONFIG_TEST,
      properties: {
        ...getDestinationTypeTrackingProps(),
        ...getHevoContextTrackingProps()
      }
    });
  };

  const onTestConnectionFormError = (invalidFields: string[]) => {
    if (invalidFields?.length) {
      _analyticsTracker.eventTrack({
        action: TRACKER_DEST_CONFIG_TEST_FORM_ERROR,
        properties: {
          invalidFields: invalidFields.join(','),
          ...getDestinationTypeTrackingProps(),
          ...getHevoContextTrackingProps()
        }
      });
    }
  };

  const testConnection = () => {
    _testConnection().pipe(take(1), takeUntil(_destroyed$.current)).subscribe();
  };

  const onSettingsSubmitClick = () => {
    dispatch({ type: ActionKind.SET_FORM_SUCCESS, data: undefined });
    _clearFormErrorMetaData();

    _analyticsTracker.eventTrack({
      action: TRACKER_DEST_CONFIG_SUBMIT,
      properties: {
        ...getDestinationTypeTrackingProps(),
        ...getHevoContextTrackingProps()
      }
    });
  };

  const onSettingsSubmitFormError = useCallback(
    (invalidFields: string[]) => {
      if (invalidFields?.length) {
        _analyticsTracker.eventTrack({
          action: TRACKER_DEST_CONFIG_SUBMIT_FORM_ERROR,
          properties: {
            invalidFields: invalidFields.join(','),
            ...getDestinationTypeTrackingProps(),
            ...getHevoContextTrackingProps()
          }
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_analyticsTracker]
  );

  const settingsSubmit = () => {
    const request$ = updateConnection ? _confirmAndEditDestination() : _createDestination();

    request$.pipe(take(1), takeUntil(_destroyed$.current)).subscribe();
  };

  const formErrorClick = () => {
    _analyticsTracker.eventTrack({
      action: TRACKER_SOURCE_CONFIG_SUBMIT_FORM_ERROR_CLICK,
      properties: {
        ...getSubmissionErrorTrackingData(formError),
        formErrorFor
      }
    });
  };

  const reauthorise = () => {
    _authService
      .reAuthoriseAccount(
        destinationTypeIdentifier,
        destination.formData.oauthTokenId,
        window.location.href,
        window.location.href
      )
      .pipe(takeUntil(_destroyed$.current))
      .subscribe((res: any) => {
        if (res && res.data && res.data.consent_url) {
          window.location.assign(res.data.consent_url);
          return;
        }
        toaster.pop('error', undefined, 'Couldn\'t reauthorize this account');
      });
  };

  const _clearFormErrorMetaData = () => {
    dispatch({
      type: ActionKind.CLEAR_FORM_ERROR_METADATA
    });
  };

  const _updateFormErrorMetaData = async (error: any, errorFor: SettingsFormSubmissionMode) => {
    dispatch({
      type: ActionKind.UPDATE_FORM_ERROR_METADATA,
      data: {
        formError: error,
        formErrorCode: getBackendErrorCodeFromObj(error),
        formErrorFor: errorFor
      }
    });
  };

  const _createDestination = (skipErrors = false) => {
    dispatch({ type: ActionKind.SET_SUBMITTING, data: true });

    _analyticsTracker.eventTrack({
      action: TRACKER_DEST_CONFIG_SUBMIT_START,
      properties: {
        ...getDestinationTypeTrackingProps(),
        ...getHevoContextTrackingProps()
      }
    });

    return _destinationSettingService
      .resolveDependenciesBeforeSaving(destinationTypeIdentifier, destination.formData)
      .pipe(
        switchMap((updatedFormData: object) => {
          onFormDataChanges({ ...destination.formData, ...updatedFormData });

          return _destinationService
            .addDestination(
              getDestinationConfigFlatModel(destination),
              destinationTypeIdentifier,
              skipErrors,
              isDestinationModeWarehouse,
              draftDestination?.id || getDraftDestinationDataFromStorage()?.id
            )
            .pipe(
              tap((resp: any) => {
                dispatch({ type: ActionKind.SET_SUBMITTING, data: false });

                // delete draft if there is one and clear local storage for draftDest
                clearDraftDestinationDataFromStorage();

                _destinationConfigSubmitSuccess(resp.data);
              }),
              catchError((error: any) => {
                if (destination.type === 'BIGQUERY') {
                  scrollDocOnBigQueryPermissionError(error);
                }
                dispatch({ type: ActionKind.SET_SUBMITTING, data: false });

                const testErrorsHandled = _handleTestConnectionError(
                  error,
                  SettingsFormSubmissionMode.CONTINUE
                );

                if (!testErrorsHandled) {
                  _updateFormErrorMetaData(error, SettingsFormSubmissionMode.CONTINUE);

                  _analyticsTracker.eventTrack({
                    action: TRACKER_DEST_CONFIG_SUBMIT_FAILED,
                    properties: getSubmissionErrorTrackingData(error)
                  });
                }

                onConnectionError(error);

                return of(null);
              })
            );
        }),
        catchError(err => {
          dispatch({ type: ActionKind.SET_SUBMITTING, data: false });

          _updateFormErrorMetaData(err, SettingsFormSubmissionMode.CONTINUE);
          onConnectionError(err);

          return of(null);
        })
      );
  };

  const _editDestination = (skipErrors = false, skipWarnings = false) =>
    of([]).pipe(
      switchMap(() => {
        dispatch({ type: ActionKind.SET_SUBMITTING, data: true });

        _analyticsTracker.eventTrack({
          action: TRACKER_DEST_CONFIG_SUBMIT_START,
          properties: {
            ...getDestinationTypeTrackingProps(),
            ...getHevoContextTrackingProps()
          }
        });

        return _destinationService.editDestination(
          getDestinationConfigFlatModel(destination),
          destinationTypeIdentifier,
          skipErrors,
          skipWarnings
        );
      }),
      switchMap(resp => {
        dispatch({ type: ActionKind.SET_SUBMITTING, data: false });

        if (resp && resp.modified_fields?.length) {
          _analyticsTracker.eventTrack({
            action: TRACKER_DEST_CONFIG_UPDATE_WARNING
          });

          setDestinationUpdateWarningConfig({
            visible: true,
            modifiedFields: resp.modified_fields,
            destinationFlatModel: getDestinationConfigFlatModel(destination),
            destinationTypeIdentifier,
            skipErrors
          });

          const warningSubject: Observable<any> = _destinationUpdateWarningResponse$.current;

          return warningSubject.pipe(
            switchMap(res => {
              if (typeof res === 'boolean' || typeof res === 'undefined') {
                if (res) {
                  _destinationConfigSubmitSuccess();
                }

                return of(null);
              }

              return throwError(res);
            })
          );
        }
        _destinationConfigSubmitSuccess();
        return of(null);
      }),
      catchError((error: any) => {
        if (destination.type === 'BIGQUERY') {
          scrollDocOnBigQueryPermissionError(error);
        }
        dispatch({ type: ActionKind.SET_SUBMITTING, data: false });

        const testErrorsHandled = _handleTestConnectionError(
          error,
          SettingsFormSubmissionMode.EDIT
        );
        if (!testErrorsHandled) {
          _updateFormErrorMetaData(error, SettingsFormSubmissionMode.EDIT);

          _analyticsTracker.eventTrack({
            action: TRACKER_DEST_CONFIG_SUBMIT_FAILED,
            properties: getSubmissionErrorTrackingData(error)
          });
        }

        onConnectionError(error);

        return of(null);
      })
    );

  const _testConnection = (skipErrors = false) => {
    dispatch({ type: ActionKind.SET_CONNECTING, data: true });

    _analyticsTracker.eventTrack({
      action: TRACKER_DEST_CONFIG_TEST_START,
      properties: {
        ...getDestinationTypeTrackingProps(),
        ...getHevoContextTrackingProps()
      }
    });

    return _destinationSettingService
      .resolveDependenciesBeforeTesting(
        destinationTypeIdentifier,
        destination.formData,
        updateConnection
      )
      .pipe(
        switchMap((updatedFormData: object) => {
          onFormDataChanges({ ...destination.formData, ...updatedFormData });
          return _destinationService
            .testConnection(
              getDestinationConfigFlatModel(destination),
              destinationTypeIdentifier,
              skipErrors,
              isDestinationModeWarehouse,
              draftDestination?.id || getDraftDestinationDataFromStorage()?.id
            )
            .pipe(
              tap((resp: any) => {
                dispatch({
                  type: ActionKind.CONNECTION_SUCCESS
                });

                _analyticsTracker.eventTrack({
                  action: TRACKER_DEST_CONFIG_TEST_SUCCESS,
                  properties: {
                    ...getDestinationTypeTrackingProps(),
                    ...getHevoContextTrackingProps()
                  }
                });
                onConnectionSuccess();
              }),
              catchError((error: any) => {
                if (destination.type === 'BIGQUERY') {
                  scrollDocOnBigQueryPermissionError(error);
                }
                dispatch({ type: ActionKind.SET_CONNECTING, data: false });

                const testErrorsHandled = _handleTestConnectionError(
                  error,
                  SettingsFormSubmissionMode.TEST
                );

                if (!testErrorsHandled) {
                  _updateFormErrorMetaData(error, SettingsFormSubmissionMode.TEST);

                  _analyticsTracker.eventTrack({
                    action: TRACKER_DEST_CONFIG_TEST_FAIL,
                    properties: getSubmissionErrorTrackingData(error)
                  });
                }

                onConnectionError(error);

                return of(null);
              })
            );
        }),
        catchError(err => {
          dispatch({ type: ActionKind.SET_CONNECTING, data: false });
          _updateFormErrorMetaData(err, SettingsFormSubmissionMode.TEST);
          onConnectionError(err);

          return of(null);
        })
      );
  };

  const _handleTestConnectionError = (error: any, submissionMode: SettingsFormSubmissionMode) => {
    const errorMsg = getErrorMessageFromObj(error);
    const isBadRequest = error.status === 400;

    let errorMsgParsed;
    try {
      errorMsgParsed = JSON.parse(errorMsg);
    } catch (e) {
      errorMsgParsed = {};
    }

    if (isBadRequest && _isErrorStatusActionable(errorMsgParsed.status)) {
      _doHandleTestConnectionError(errorMsgParsed, submissionMode);
      return true;
    }

    return false;
  };

  const _isErrorStatusActionable = (status: any) =>
    Object.values(DestinationConnectionActionableErrorStatus).includes(status);

  const _doHandleTestConnectionError = (
    errorMessage: any,
    submissionMode: SettingsFormSubmissionMode
  ) => {
    confirm({
      title: errorMessage.message.title,
      body: errorMessage.message.description,
      positiveButtonText: 'Yes, Go ahead',
      negativeButtonText: 'No'
    }).then((res: any) => {
      if (res) {
        onFormDataChanges({ ...destination.formData });

        if (submissionMode === SettingsFormSubmissionMode.CONTINUE) {
          _createDestination(true).subscribe();
        }

        if (submissionMode === SettingsFormSubmissionMode.EDIT) {
          _editDestination(true, true).subscribe();
        }

        if (submissionMode === SettingsFormSubmissionMode.TEST) {
          _testConnection(true).subscribe();
        }
      }
    });
  };

  const _destinationConfigSubmitSuccess = (data?: any) => {
    onConnectionSuccess();

    _analyticsTracker.eventTrack({
      action: TRACKER_DEST_CONFIG_SUBMIT_SUCCESS,
      properties: {
        ...getDestinationTypeTrackingProps(),
        ...getHevoContextTrackingProps()
      }
    });

    onSubmit(data);
  };

  const _confirmAndEditDestination = () => {
    return fromPromise(
      confirmV2({
        iconName: 'warning',
        iconContainerClass: 'warning',
        title: 'Need your attention',
        body: 'An update in the configuration will be reflected in' +
          ' all the associated Pipelines/Activations wherever it is being used.'
      })
    ).pipe(
      switchMap((confirm) => {
        if (confirm) {
          return _editDestination();
        }

        return of(null);
      })
    );
  };

  const scrollDocOnBigQueryPermissionError = (error: any) => {
    if (getBackendErrorCodeFromObj(error) === 1011) {
      let setupGuideLink;
      const trackingParams: SetupGuideLinkTrackingParams = {
        medium: 'program',
        sourceSection: 'big-query-destination',
        label: 'big-query-auth-permission-error',
        properties: {
          ...getDestinationTypeTrackingProps(),
          ...getHevoContextTrackingProps()
        }
      };

      if (destination.formData.oauthTokenId) {
        setupGuideLink = '/destinations/data-warehouses/google-bigquery/#using-user-accounts';
      } else {
        setupGuideLink =
          '/destinations/data-warehouses/google-bigquery/#using-google-service-accounts';
      }

      _setupGuideCommService.goToSection(setupGuideLink, {
        ...trackingParams
      });
    }
  };

  const onUpdateWarningDialogClose = (res: any) => {
    _destinationUpdateWarningResponse$.current.next(res);
    setDestinationUpdateWarningConfig({
      ...destinationUpdateWarningConfig,
      visible: false
    });
  };

  const testBtnText = connecting ? 'Connecting' : 'Test Connection';
  const submitBtnText = submitting ? `Saving` : `Save & Continue`;
  const canShowSubmissionOverlay = connecting || submitting;

  const destinationTypeMetaData = DESTINATION_TYPES[destinationTypeIdentifier];
  const destinationModeKeywords = getDestinationModeKeywords(destinationMode);
  const destinationNameFieldLabel = `${destinationModeKeywords.singular} Name`;
  const isDestinationModeWarehouse = destinationMode === DestinationMode.WAREHOUSE;
  const canReauthoriseAccount = 'oauthTokenId' in destination.formData && updateConnection;

  return (
    <>
      <ConfigDestinationType
        destinationTypeIdentifier={destination.type}
        rawDestinationType={destinationType}
        formData={destination.formData || {}}
        formMetaData={destination.metaData}
        isEditing={updateConnection}
        formError={formError ? getErrorMessageFromObj(formError) : null}
        formSuccess={formSuccess}
        submitBtnText={submitBtnText}
        testBtnText={testBtnText}
        submitting={submitting}
        reauthoriseRequest={reauthorise}
        canReauthoriseAccount={canReauthoriseAccount}
        connecting={connecting}
        canShowSubmissionOverlay={canShowSubmissionOverlay}
        destinationMode={destinationMode}
        destinationTypeMetaData={destinationTypeMetaData}
        destinationModeKeywords={destinationModeKeywords}
        destinationNameFieldLabel={destinationNameFieldLabel}
        frozenFields={frozenFields}
        onFormChanges={onFormDataChanges}
        onContinue={settingsSubmit}
        onContinueClick={onSettingsSubmitClick}
        onContinueFormError={onSettingsSubmitFormError}
        onTestConnectionFormError={onTestConnectionFormError}
        onTestConnectionClick={onTestConnectionClick}
        onTestConnection={testConnection}
        markFormDirty={onFormDirty}
        setFormError={data => dispatch({ type: ActionKind.SET_FORM_ERROR, data })}
        setFormSuccess={data => {
          dispatch({ type: ActionKind.SET_FORM_SUCCESS, data });
        }}
        savedDestinationName={savedDestinationName}
        formErrorCode={formErrorCode}
        formErrorClick={formErrorClick}
        formInteraction={onFormFieldChange}
        formFieldFocusIn={onFormFieldFocusIn}
        hevoEntityFor={hevoEntityFor}
        draftDestination={draftDestination}
        selectedExistingDestination={selectedExistingDestination}
        onInviteMember={onInviteMember}
        experiments={{
          isSnowflakeAutoFillExperimentVariant
        }}
      />

      <DestinationUpdateWarningDialog
        destinationUpdateWarningConfig={destinationUpdateWarningConfig}
        onClose={res => onUpdateWarningDialogClose(res)}
      />
    </>
  );
}
